import React, { useState, useEffect, useRef } from 'react';
import './styles.css';
import { locationService } from '../../services/locationService';
import { usePlanFeatures } from '../../contexts/PlanFeaturesContext';
import locationIcon from '../../images/location.svg';
import hazardIcon from '../../images/hazardIcon.svg';
import { useModalFocusReturn } from '../../hooks/useModalFocusReturn';
import { resolveExpectedStateCode, isLocationInExpectedState, STATE_ABBREVIATION_TO_NAME, STATE_BOUNDING_BOXES, isSuggestionOutOfState } from '../../utils/locationStateValidation';
import ApiErrorBanner from '../errorMessages/ApiErrorBanner';
import { ERROR_TYPES, getErrorMessage } from '../errorMessages/errorMessages.constants';


const LocationPanel = ({ isOpen, onClose, onLocationSelect, initialStateError = '' }) => {
    const [searchValue, setSearchValue] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isLoadingLocation, setIsLoadingLocation] = useState(false);
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
    const [mapsError, setMapsError] = useState('');
    const [stateError, setStateError] = useState('');
    const debounceTimer = useRef(null);
    // Tags each fetchSuggestions() call with an incrementing token so an older, slower
    // request can never overwrite a newer one's results if responses arrive out of
    // order (common on mobile/cellular connections with inconsistent latency — the
    // debounce timer only cancels a not-yet-FIRED timeout, it does nothing to a fetch
    // that's already in flight, so pausing while typing more than once can leave
    // multiple requests in flight at once). See fetchSuggestions() below for how this
    // is used. This was the cause of suggestions briefly appearing then disappearing
    // ("flickering") specifically reported on mobile/iPhone testing.
    const latestRequestId = useRef(0);
    const panelRef = useRef(null);
    const { planFeatures, environmentId, planCode, getCentralZip } = usePlanFeatures();
    // Caches the plan's central-zip coordinates so we only geocode it once per panel
    // session rather than on every keystroke — see getCentralCoords() below.
    const centralCoordsRef = useRef(null);

    // Restore focus to whatever triggered this panel once it closes.
    useModalFocusReturn(isOpen);

    // Mirrors a console.log/warn/error call to the real console. `level` is
    // 'log' | 'warn' | 'error'.
    // Builds a plain, JSON-serializable snapshot of an error/exception, including
    // properties JSON.stringify would normally lose. Error/DOMException/gRPC-Web
    // RpcError instances keep name/message/stack as non-enumerable own properties, so a
    // naive JSON.stringify(err) collapses to "{}" — this pulls those out explicitly,
    // plus any extra enumerable own properties (e.g. Google's RpcError often carries a
    // `code` and/or `metadata`/`status` alongside message) so nothing is silently lost
    // when a tester's raw log is forwarded to the team that owns the Google Cloud key.
    const dumpError = (err) => {
        if (!(err instanceof Object)) return err;
        const snapshot = {};
        if (err.name !== undefined) snapshot.name = err.name;
        if (err.message !== undefined) snapshot.message = err.message;
        if (err.code !== undefined) snapshot.code = err.code;
        if (err.status !== undefined) snapshot.status = err.status;
        Object.keys(err).forEach((key) => {
            if (!(key in snapshot)) snapshot[key] = err[key];
        });
        if (err.stack) snapshot.stack = err.stack;
        return snapshot;
    };

    const logDebug = (level, ...args) => {
        (console[level] || console.log)(...args);
    };

    // Load Google Maps script with places library if not already loaded
    const loadGoogleMapsScript = () => {
        return new Promise((resolve, reject) => {

            // ① Already fully ready — resolve immediately
            if (window.google?.maps?.places) {
                resolve();
                return;
            }

            // Helper: polls until window.google.maps.places is available
            const pollForPlaces = (timeoutMs = 15000) => {
                const start = Date.now();
                const interval = setInterval(() => {
                    if (window.google?.maps?.places) {
                        clearInterval(interval);
                        resolve();
                    } else if (Date.now() - start > timeoutMs) {
                        clearInterval(interval);
                        reject(new Error('Google Maps places library timed out'));
                    }
                }, 150);
            };

            // ② Script tag already exists (loading or loaded) — just poll for places
            if (document.querySelector('script[src*="maps.googleapis.com"]')) {
                logDebug('log', '⏳ LocationPanel: Maps script exists, waiting for places...');
                pollForPlaces();
                return;
            }

            // ③ No script yet — inject it, then poll for places
      const apiKey = window.APP_CONFIG?.VITE_GOOGLE_MAPS_API_KEY || import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

            if (!apiKey) {
                reject(new Error('Google Maps API key not found'));
                return;
            }

            logDebug('log', '📦 LocationPanel: Injecting Google Maps script...');
            const script = document.createElement('script');
            // loading=async means places library loads after onload fires, so we must poll
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=weekly&libraries=places&loading=async`;
            script.async = true;
            script.defer = true;
            script.onload  = () => {
                logDebug('log', '✅ LocationPanel: Script loaded, polling for places library...');
                pollForPlaces();
            };
            script.onerror = () => reject(new Error('Failed to load Google Maps script'));
            document.head.appendChild(script);
        });
    };

    // Initialize and verify Google Maps whenever panel opens
    useEffect(() => {
        if (!isOpen) return;

        setMapsError('');

        loadGoogleMapsScript()
            .then(() => {
                logDebug('log', '✅ Google Maps Places ready');
            })
            .catch((err) => {
                logDebug('error', '❌ Google Maps failed to load:', err.message);
                // Use the shared Google-location-services error copy (see
                // reports/SPECIFIC_ERROR_MESSAGES_PLAN.md) instead of a bespoke string,
                // so this matches the same message shown elsewhere for map/geocode
                // failures. ZIP/city/county text entry remains fully usable below.
                setMapsError(getErrorMessage(ERROR_TYPES.LOCATION));
            });
    }, [isOpen]);

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, []);

    // Clear errors when panel opens — except when the caller (e.g. search.jsx, after browser
    // geolocation resolved outside the plan's state or failed to produce a usable address)
    // explicitly opened this panel WITH an initialStateError already queued up, so the user
    // sees an actionable message immediately instead of a silently-empty panel.
    useEffect(() => {
        if (isOpen) {
            setStateError(initialStateError || '');
        }
    }, [isOpen, initialStateError]);

    // Focus trap: keep keyboard focus inside the panel while it is open.
    // Re-queries the panel's focusable elements on every Tab press (rather than once,
    // when this effect first runs) since the panel's contents change while it's open —
    // e.g. the "Search" submit button only mounts once searchValue is non-empty, the
    // suggestions list mounts/unmounts as the user types, and the error message
    // mounts/unmounts. Caching a single first/lastElement pair at effect-run time meant
    // those references quickly went stale: once the actual last focusable element in
    // the DOM changed, document.activeElement could never match the stale cached
    // `lastElement` again, so Tab silently stopped wrapping back to the first element
    // and focus escaped the panel into the page behind it (reported: "using tab through
    // the elements the focus goes back to the Find care page, instead of cycling only
    // inside the expanded side bar").
    useEffect(() => {
        if (isOpen && panelRef.current) {
            const handleTabKey = (e) => {
                if (e.key !== 'Tab') return;

                // IMPORTANT: filter out elements the browser's native Tab order would
                // itself skip (disabled controls, or anything hidden), not just query
                // for tag/attribute matches. querySelectorAll has no concept of
                // "disabled" — it happily returns a disabled <button>. The panel's own
                // "Apply new location" footer button is disabled by default (until
                // searchValue is non-empty, see its `disabled={... || !searchValue.trim()}`
                // prop below), so on the very common case of opening the panel and
                // immediately tabbing through WITHOUT having typed anything yet, the
                // real last element the browser will actually focus is the "main site"
                // link a few elements before it — but the old code still treated the
                // disabled Apply button as `lastElement`. Since a disabled element can
                // never become `document.activeElement`, the forward-wrap check below
                // could never match, so pressing Tab from the true last focusable
                // element (the link) fell through to native browser behavior and
                // escaped the panel into the page behind it — exactly the reported
                // "focus goes back to the Find care page" bug.
                const focusableElements = Array.from(
                    panelRef.current.querySelectorAll(
                        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                    )
                ).filter((el) => !el.disabled && el.offsetParent !== null);
                if (focusableElements.length === 0) return;

                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                if (e.shiftKey && document.activeElement === firstElement) {
                    e.preventDefault();
                    lastElement?.focus();
                } else if (!e.shiftKey && document.activeElement === lastElement) {
                    e.preventDefault();
                    firstElement?.focus();
                }
            };

            panelRef.current.addEventListener('keydown', handleTabKey);
            return () => {
                panelRef.current?.removeEventListener('keydown', handleTabKey);
            };
        }
    }, [isOpen]);

    // Belt-and-suspenders backstop for the Tab-boundary trap above. That approach relies
    // on correctly identifying the exact first/last focusable element and matching
    // document.activeElement against it at the moment Tab is pressed — any edge case that
    // throws off that match (timing, an element that's focusable-but-momentarily-filtered,
    // a suggestion/error row mounting or unmounting between the keydown firing and the
    // browser actually moving focus, etc.) lets focus silently escape into the page behind
    // the panel with no further correction. This effect instead checks the RESULT of every
    // focus change: whenever isOpen and the newly-focused element is anything other than a
    // descendant of the panel itself (including the case where focus lands on <body>, e.g.
    // via a removed/disabled element), focus is immediately pulled back inside — to the
    // element closest to the natural fallback (the panel's autoFocus search input) rather
    // than silently doing nothing. Testers reported the boundary-only trap above still let
    // focus reach the page's search bar after "several tabs past Edit location", which this
    // guarantees can no longer happen regardless of the root cause.
    useEffect(() => {
        if (!isOpen) return;

        const handleFocusIn = (e) => {
            if (!panelRef.current) return;
            if (panelRef.current.contains(e.target)) return;
            // Focus escaped the panel — pull it back to the search input (or, failing
            // that, the panel container itself so it's at least still trapped).
            const fallback = panelRef.current.querySelector('#location-search-input') || panelRef.current;
            fallback?.focus();
        };

        document.addEventListener('focusin', handleFocusIn);
        return () => {
            document.removeEventListener('focusin', handleFocusIn);
        };
    }, [isOpen]);

    // Resolves the definitive 2-letter state/env code for the current plan, for both the
    // autocomplete restriction below and validateLocationState's post-search check.
    //
    // resolveStateCode/validateLocationState are now shared via
    // ../../utils/locationStateValidation (resolveExpectedStateCode/isLocationInExpectedState)
    // so every location-resolution path across the app (this manual-search flow, plus
    // search.jsx/results.jsx's browser-geolocation, IP-geolocation, and central-zip
    // fallbacks) validates against the exact same logic instead of each maintaining its own
    // copy that could drift out of sync.
    const resolveStateCode = () => resolveExpectedStateCode(planCode, planFeatures);

    // Validate that location is within the plan's state
    const validateLocationState = (locationData) => {
        if (!locationData?.stateCode) {
            // Previously this returned false SILENTLY — no stateError was ever set, so
            // handleUseCurrentLocation's caller just saw isLoadingLocation flip back off with
            // no explanation at all (browser location resolved, but reverse-geocoding couldn't
            // extract a usable city/state from it — e.g. a rural/edge-case coordinate, or
            // being far enough outside the plan's state that the reverse-geocode API returned
            // an address shape this parser doesn't recognize). Give the user an actionable
            // message and point them at manual entry instead of a silent dead end.
            logDebug('warn', '⚠️ No state code in location data');
            setStateError('We couldn\'t determine your location\'s state. Please enter your address manually below.');
            return false;
        }

        const expectedState = resolveStateCode();
        // Human-readable name for the error message — use the full state name from the
        // shared crosswalk (e.g. "Arizona") rather than `planDescription`, which is the
        // plan's BUSINESS/marketing name (e.g. "Mercy Care" for AZ plans) and isn't a state
        // name at all — that mismatch was the reported AZ bug ("You can only search in
        // Mercy Care." instead of "...in Arizona."). Falls back to the 2-letter code, then
        // `planDescription`, then a generic phrase if the code can't be resolved to a name.
        const planDisplayName = STATE_ABBREVIATION_TO_NAME[expectedState] || expectedState || planFeatures?.planDescription || 'your plan';

        logDebug('log', '🔍 Validating location state:', {
            locationState: locationData.stateCode?.toUpperCase(),
            expectedState,
            planDisplayName
        });

        if (!isLocationInExpectedState(locationData, expectedState)) {
            setStateError(`You can only search in ${planDisplayName}.`);
            return false;
        }

        setStateError('');
        return true;
    };

    // Resolves lat/lng for the plan's central zip and caches it for the life of this
    // panel instance. Without this, Google's Autocomplete API silently biases
    // suggestions toward the requester's IP-derived location (common on VPNs/test
    // environments where the tester's real IP has nothing to do with the plan's
    // service area) — meaning testers physically outside the plan's state/region
    // saw mostly local-to-them results instead of results relevant to the plan.
    // Passing an explicit locationBias centered on the plan's own central zip
    // overrides that IP-based guess so autocomplete favors the plan's actual
    // coverage area for everyone, regardless of where they're physically testing from.
    const getCentralCoords = async () => {
        if (centralCoordsRef.current) return centralCoordsRef.current;

        const centralZip = getCentralZip?.();
        if (!centralZip) return null;

        try {
            // Bound this geocode to the plan's expected state (resolved from the URL's
            // :planCode segment via resolveStateCode/resolveExpectedStateCode above) —
            // without it, a bare zip-only query passed to Place.searchByText has no
            // location bias/restriction whatsoever (that's the whole reason this
            // function exists — to CREATE that bias for later autocomplete calls), so
            // it can itself match a same-numbered ZIP/place in a completely different
            // state, silently resolving the plan's own central zip to the wrong state's
            // coordinates and poisoning every autocomplete suggestion biased off of it.
            const coordsData = await locationService.geocodeGoogle({ zip: centralZip, state: resolveStateCode() || undefined });
            if (coordsData?.latitude && coordsData?.longitude) {
                centralCoordsRef.current = { lat: coordsData.latitude, lng: coordsData.longitude };
                logDebug('log', '📍 Cached central-zip coords for autocomplete bias:', centralCoordsRef.current);
                return centralCoordsRef.current;
            }
        } catch (err) {
            // Dump the actual referrer/origin this browser sent alongside the full raw
            // error (not just message) — needed as hard evidence for the team that owns
            // the Google Cloud API key restrictions, since Safari/WebKit and iCloud
            // Private Relay can send a different (or no) Referer header than desktop
            // browsers, which is the leading theory for why this only reproduces on
            // Apple devices. See matching diagnostic dump in fetchSuggestions' catch below.
            logDebug('warn', '⚠️ Could not geocode central zip for autocomplete bias:', {
                documentReferrer: document.referrer || '(empty)',
                windowOrigin: window.location.origin,
                windowHref: window.location.href,
                userAgent: navigator.userAgent,
                rawError: dumpError(err),
            });
        }
        return null;
    };

    const fetchSuggestions = async (value) => {
        if (!window.google?.maps?.places) {
            logDebug('warn', '⚠️ Google Maps Places not available');
            return;
        }

        // Claim this call as the newest request. Any earlier in-flight call will see
        // its own captured `requestId` no longer match `latestRequestId.current` once
        // this line runs, and will bail out without touching state when it eventually
        // resolves — see the guards below.
        const requestId = ++latestRequestId.current;

        setIsLoadingSuggestions(true);

        // See resolveStateCode() above for why this is sourced from `planCode` first.
        const stateCode = resolveStateCode();
        
        if (stateCode) {
            logDebug('log', '🔍 Restricting autocomplete to state:', stateCode);
        } else {
            logDebug('log', '🔍 No valid state code available, showing all US suggestions');
        }

        // Bias (not restrict) results toward the plan's own service area instead of
        // letting Google fall back to the requester's IP-derived location — see
        // getCentralCoords() above for why this matters for testers.
        const centralCoords = await getCentralCoords();

        try {
            // ── PRIMARY: New AutocompleteSuggestion API (Places API New) ─────
            // Requires "Places API (New)" enabled in Google Cloud Console.
            if (window.google.maps.places.AutocompleteSuggestion) {
                const requestOptions = {
                    input: value,
                    includedRegionCodes: ['us']
                };

                // HARD restriction to the plan's expected state's bounding box takes
                // priority over the soft central-zip bias below — Google's API rejects any
                // request that sets BOTH locationBias and locationRestriction at once, so
                // these are mutually exclusive, not additive. Reported bug: "still seeing a
                // lot of results that aren't the correct state in the edit location search"
                // — the 50km locationBias radius below only nudges ranking near the plan's
                // central zip, it never excludes anything, and 50km covers only a small
                // fraction of most states' actual area, so the vast majority of in-state
                // addresses got no boost at all while genuinely out-of-state suggestions
                // were never excluded outright. locationRestriction, by contrast, is a hard
                // filter — Google will only return suggestions whose location falls inside
                // this rectangle at all, which (unlike the 50km circle) can be sized to
                // cover the ENTIRE state. Deliberately generous (see STATE_BOUNDING_BOXES)
                // rather than a precise border, so it never excludes a real in-state
                // address near the state line.
                if (stateCode && STATE_BOUNDING_BOXES[stateCode]) {
                    const [south, west, north, east] = STATE_BOUNDING_BOXES[stateCode];
                    requestOptions.locationRestriction = {
                        south, west, north, east
                    };
                } else if (centralCoords) {
                    // Only fall back to the soft central-zip bias when we don't have a
                    // known state bounding box to hard-restrict to at all.
                    // 50,000m is Google's HARD MAXIMUM for a circle radius on both the
                    // New Places API and the legacy AutocompleteService — anything above
                    // that throws "Invalid circle.radius" and the request fails outright
                    // (this previously used 80,000m/80km, which silently broke every
                    // autocomplete call once centralCoords resolved, explaining the
                    // suggestions "flickering on then off": the debounced call would
                    // start, immediately throw, and fall through to the catch block's
                    // setSuggestions([])). Capped at the max allowed 50,000m (~31mi).
                    requestOptions.locationBias = {
                        radius: 50000,
                        center: centralCoords
                    };
                }

                // Add state restriction if we have a valid state code
                if (stateCode) {
                    // See the isSuggestionOutOfState() filter applied to `mapped` below —
                    // this log is kept only for parity with the diagnostic logging
                    // elsewhere in this function.
                    logDebug('log', '🔍 State code available (narrow out-of-state suggestion filter active):', stateCode);
                }

                const { suggestions: newSuggestions } =
                    await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(requestOptions);

                let mapped = (newSuggestions || []).map((s) => ({
                    place_id:              s.placePrediction?.placeId || '',
                    description:           s.placePrediction?.text?.text || '',
                    structured_formatting: {
                        main_text:      s.placePrediction?.mainText?.text || '',
                        secondary_text: s.placePrediction?.secondaryText?.text || ''
                    },
                    _placePrediction: s.placePrediction
                })).filter(s => s.description);

                // Narrow, unambiguous-only state filter — see isSuggestionOutOfState's doc
                // for why this is deliberately conservative. The hard locationRestriction
                // rectangle above always over-includes some neighboring-state territory
                // near the border (rectangles can't match a real state border), so a
                // clearly-different-state suggestion can still come back from Google even
                // with that restriction in place. This only drops a suggestion when its
                // own secondary_text unambiguously names a different state; anything
                // ambiguous (no recognizable state text, ZIP-only predictions, unusual
                // formats) is left alone/shown — see the broader text-filtering attempt
                // that WAS removed from here (note preserved below) for why anything less
                // conservative than this caused valid ZIP/address suggestions to vanish.
                if (stateCode) {
                    mapped = mapped.filter(
                        s => !isSuggestionOutOfState(s.structured_formatting?.secondary_text, stateCode)
                    );
                }

                // NOTE: Beyond the narrow isSuggestionOutOfState() filter just above, we do
                // NOT do any further/broader state filtering here. Google's secondary_text
                // formatting for a given prediction is inconsistent (sometimes "City, ST,
                // USA", sometimes "City, Full State Name, USA", sometimes just "USA" for
                // ZIP-only predictions, and sometimes it omits state info altogether for
                // certain address types) — a broader pattern-matching filter was tried here
                // previously and removed because it risked dropping perfectly valid results:
                // ZIP codes returning zero suggestions, and some valid addresses only
                // appearing once "PA" was manually appended to the search term. State
                // correctness is still enforced properly, AFTER a specific place is chosen,
                // by validateLocationState() below (which uses the actual resolved
                // stateCode/postal data from the geocoded result, not fragile text
                // matching) — that remains the authoritative check; the filter above is
                // just an extra pass to catch the most obvious, unambiguous mismatches
                // before they ever show up in the dropdown.

                // Bail out silently if a NEWER request has been kicked off since this one
                // started — applying this (older) response now would overwrite whatever
                // the newer, still-in-flight (or already-resolved) request produces,
                // which is exactly what caused suggestions to flicker in and then vanish.
                if (requestId !== latestRequestId.current) {
                    logDebug('log', '⏭️ Discarding stale suggestions response (Places API New)');
                    return mapped;
                }

                setIsLoadingSuggestions(false);
                logDebug('log', `📍 ${mapped.length} suggestions found (Places API New)`);
                setSuggestions(mapped);
                return mapped;
            }

            // ── FALLBACK: Legacy AutocompleteService ─────────────────────────
            if (typeof window.google.maps.places.AutocompleteService === 'function') {
                const service = new window.google.maps.places.AutocompleteService();

                // NOTE: We intentionally do NOT set componentRestrictions.administrativeArea
                // here, nor do we apply any BROAD post-filter of predictions by state text
                // (see the matching note in the New Places API branch above for why:
                // Google's state formatting is inconsistent enough that a broad
                // restriction/filter here was silently dropping valid ZIP and address
                // suggestions). We DO still apply the same narrow isSuggestionOutOfState()
                // filter below, just like the New Places API branch, to catch the most
                // obvious/unambiguous mismatches that slip through the bounds restriction.
                // State correctness is enforced after selection via validateLocationState()
                // using the actual geocoded result.
                const requestOptions = {
                    input: value,
                    componentRestrictions: { country: 'us' }
                };

                if (stateCode && STATE_BOUNDING_BOXES[stateCode]) {
                    // Hard-restrict via `bounds` + `strictBounds: true` — mirrors the New
                    // API's locationRestriction above (see that block's comment for why:
                    // reported bug of out-of-state suggestions still showing up). Without
                    // strictBounds:true, `bounds` on the legacy service is only a soft bias
                    // like `location`/`radius` below, not an actual restriction.
                    const [south, west, north, east] = STATE_BOUNDING_BOXES[stateCode];
                    requestOptions.bounds = new window.google.maps.LatLngBounds(
                        new window.google.maps.LatLng(south, west),
                        new window.google.maps.LatLng(north, east)
                    );
                    requestOptions.strictBounds = true;
                } else if (centralCoords) {
                    // Legacy AutocompleteService uses `location` (LatLng-like) + `radius`
                    // (meters) for soft location biasing — same 50,000m max-radius
                    // constraint as the New API's locationBias above.
                    requestOptions.location = new window.google.maps.LatLng(centralCoords.lat, centralCoords.lng);
                    requestOptions.radius = 50000;
                }

                const predictions = await new Promise((resolve, reject) => {
                    service.getPlacePredictions(
                        requestOptions,
                        (results, status) => {
                            const { OK, ZERO_RESULTS } = window.google.maps.places.PlacesServiceStatus;
                            if (status === OK && results)    resolve(results);
                            else if (status === ZERO_RESULTS) resolve([]);
                            else reject(new Error(`AutocompleteService: ${status}`));
                        }
                    );
                });

                // Same narrow, unambiguous-only state filter as the New Places API branch
                // above — see isSuggestionOutOfState's doc for why this is intentionally
                // conservative (fails open on anything ambiguous).
                const filteredPredictions = stateCode
                    ? predictions.filter(
                        p => !isSuggestionOutOfState(p.structured_formatting?.secondary_text, stateCode)
                      )
                    : predictions;

                // Same stale-response guard as the New Places API branch above.
                if (requestId !== latestRequestId.current) {
                    logDebug('log', '⏭️ Discarding stale suggestions response (legacy)');
                    return filteredPredictions;
                }

                setIsLoadingSuggestions(false);
                logDebug('log', `📍 ${filteredPredictions.length} suggestions found (legacy)`);
                setSuggestions(filteredPredictions);
                return filteredPredictions;
            }

            throw new Error('No supported Places autocomplete API found');

        } catch (err) {
            // Log rich diagnostic detail (name/message/stack) rather than just the message —
            // this is specifically to help diagnose the "suggestions blip in then vanish"
            // reports from iPhone users we can't reproduce locally. Likely iOS/Safari-specific
            // causes for a request failing intermittently include: content-blocker Safari
            // extensions blocking Google's places.googleapis.com calls, Intelligent Tracking
            // Prevention (ITP) partitioning the session-token storage Google's Autocomplete
            // API relies on, or iCloud Private Relay changing the effective request
            // origin/IP in a way that trips Google's API-key referrer restrictions. err.name
            // (e.g. "TypeError: Failed to fetch" vs. a Google-specific status string) is the
            // key detail needed to tell these apart — capture it so it shows up in the
            // console for testers to send back to us.
            // Also dump the referrer/origin actually sent by this browser plus the FULL
            // raw error (every enumerable + known Google-error property, not just
            // name/message) — this is the hard evidence needed to get the Google Cloud
            // team to fix the API key's HTTP referrer allowlist, since they won't act on
            // a paraphrase alone.
            logDebug('error', '❌ fetchSuggestions error:', {
                documentReferrer: document.referrer || '(empty)',
                windowOrigin: window.location.origin,
                windowHref: window.location.href,
                userAgent: navigator.userAgent,
                rawError: dumpError(err),
            });
            // Don't clear suggestions/loading state on behalf of a request that's no
            // longer the latest one — a newer request may already be in flight (or may
            // have already succeeded), and clearing here would stomp on its results,
            // again producing the flicker-then-vanish symptom.
            if (requestId !== latestRequestId.current) {
                return [];
            }
            setIsLoadingSuggestions(false);
            setSuggestions([]);
            // Previously this failed COMPLETELY silently — suggestions just vanished with
            // no explanation, which is indistinguishable from a bug to the user (exactly
            // the "blip in and then nothing" symptom reported). Surface a visible, actionable
            // message instead so the user knows to try typing again or use manual entry,
            // and so the "Click me for logs" link becomes available (it only shows once
            // stateError is set) for them to report the real underlying error to us.
            setStateError('Address suggestions failed to load. You can try typing again, or enter your full address and press Search.');
            return [];
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchValue(value);

        // Clear any previously-shown error (e.g. from a failed "Use my current
        // location" attempt, or a prior failed manual submit) as soon as the user
        // starts typing a new location — otherwise a stale error message lingers
        // on screen indefinitely even though the user has clearly moved on to a
        // new search attempt, making it look like the new input is also failing.
        if (stateError) setStateError('');

        if (value.trim() === '' || value.trim().length < 3) {
            // Invalidate any in-flight fetchSuggestions() call too — otherwise it could
            // still resolve after this point and repopulate suggestions the user no
            // longer wants shown (e.g. they deleted back down below 3 characters).
            latestRequestId.current++;
            setSuggestions([]);
            return;
        }

        // Debounce 300ms to avoid too many API calls per keystroke
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            fetchSuggestions(value.trim());
        }, 300);
    };

    // Picks the first suggestion whose description/secondary_text mentions the plan's
    // expected state (e.g. ", PA," or ", PA " near the end, as Google formats it). Falls
    // back to the very first suggestion in the list if none explicitly mention the state
    // (some predictions — ZIP-only, etc. — omit state text; see the note in
    // fetchSuggestions() above about why we don't hard-filter suggestions by state at
    // fetch time). Final correctness is always re-checked afterward by
    // validateLocationState() once the place's real geocoded stateCode is known.
    const getFirstSuggestionInState = (suggestionList) => {
        if (!suggestionList || suggestionList.length === 0) return null;

        const expectedState = resolveStateCode();
        if (!expectedState) return suggestionList[0];

        const stateMatch = suggestionList.find((s) => {
            const text = `${s.description || ''} ${s.structured_formatting?.secondary_text || ''}`;
            const stateRegex = new RegExp(`\\b${expectedState}\\b`, 'i');
            return stateRegex.test(text);
        });

        return stateMatch || suggestionList[0];
    };

    // Replaces the old handleSearchSubmit flow (which geocoded the raw typed text via MCS,
    // ignoring the autocomplete suggestions entirely). Both the form's onSubmit and the
    // "Apply new location" footer button now route through here instead: rather than
    // geocoding whatever partial text the user has typed, this selects the best matching
    // suggestion (first one in the plan's expected state, see getFirstSuggestionInState
    // above) and runs it through the exact same handleSuggestionClick flow a manual click
    // would use — so submit/typed-Enter and click-a-suggestion behave identically.
    const handleApplySearch = async (e) => {
        if (e?.preventDefault) e.preventDefault();
        if (!searchValue.trim()) return;

        // NOTE: `suggestions` here is a React state value captured in this render's
        // closure — calling fetchSuggestions() (which calls setSuggestions() internally)
        // does NOT update this local `suggestions` variable synchronously. Using the
        // stale `suggestions` state right after triggering a fetch was why the first
        // click/submit always saw an empty list (whatever was fetched only became
        // visible on the NEXT render/call). fetchSuggestions() now returns the fetched
        // array directly, so we use that return value instead of relying on state.
        let currentSuggestions = suggestions;
        if (currentSuggestions.length === 0) {
            currentSuggestions = await fetchSuggestions(searchValue.trim());
        }

        const bestSuggestion = getFirstSuggestionInState(currentSuggestions);

        if (bestSuggestion) {
            await handleSuggestionClick(bestSuggestion);
            return;
        }

        // No suggestions available yet (e.g. Places API unavailable/still loading, or the
        // debounce/fetch hasn't resolved) — fall back to geocoding the typed text directly
        // via MCS, same as the old handleSearchSubmit did.
        setIsLoadingLocation(true);
        logDebug('log', '🔍 No suggestions available, geocoding typed text via MCS:', searchValue);

        try {
            const trimmedValue = searchValue.trim();
            // Bare 5-digit input (e.g. "19104") is a ZIP code, not a street address.
            // Sending it through the generic `address1` free-text field can fail on
            // the MCS forwardGeocode endpoint depending on how it parses the address
            // string. geocodeMCS already supports a dedicated `zip` param — route
            // ZIP-only input there instead so the backend can look it up correctly.
            const isZipOnly = /^\d{5}$/.test(trimmedValue);
            const coordsData = await locationService.geocodeMCS(
                isZipOnly ? { zip: trimmedValue } : { address1: trimmedValue }
            );

            logDebug('log', '✅ Geocoded location data:', coordsData);

            // Validate the location is in the correct state
            if (!validateLocationState(coordsData)) {
                setIsLoadingLocation(false);
                return;
            }

            // Pass the full location object with coordinates
            if (onLocationSelect) {
                onLocationSelect(coordsData);
            }

            setSuggestions([]);
            setIsLoadingLocation(false);
            onClose();
        } catch (error) {
            logDebug('error', '❌ Failed to geocode address:', error);
            setStateError('Unable to find that address. Please try selecting from the suggestions or enter a different address.');
            setIsLoadingLocation(false);
        }
    };

    const handleSuggestionClick = async (suggestion) => {
        setSearchValue(suggestion.description);
        setSuggestions([]);
        setIsLoadingLocation(true);

        logDebug('log', '📍 Getting place details for:', suggestion.description);

        try {
            // Get place details from Google to extract coordinates
            if (!window.google?.maps?.places) {
                throw new Error('Google Maps not available');
            }

            let latitude, longitude, city = '', stateCode = '', zipCode = '', formattedAddress = '';

            if (suggestion._placePrediction) {
                // ── New Places API (Place.fetchFields) ───────────────────────
                const place = suggestion._placePrediction.toPlace();
                await place.fetchFields({ fields: ['location', 'addressComponents', 'formattedAddress'] });

                logDebug('log', '✅ Place details (Places API New):', place);

                latitude  = place.location?.lat();
                longitude = place.location?.lng();
                formattedAddress = place.formattedAddress || suggestion.description;

                (place.addressComponents || []).forEach((component) => {
                    const types = component.types;
                    if (types.includes('locality'))                      city      = component.longText;
                    else if (types.includes('administrative_area_level_1')) stateCode = component.shortText;
                    else if (types.includes('postal_code'))              zipCode   = component.longText;
                });
            } else {
                // ── Legacy PlacesService.getDetails fallback ─────────────────
                const service = new window.google.maps.places.PlacesService(document.createElement('div'));

                const placeDetails = await new Promise((resolve, reject) => {
                    service.getDetails(
                        { placeId: suggestion.place_id, fields: ['geometry', 'address_components', 'formatted_address'] },
                        (place, status) => {
                            if (status === window.google.maps.places.PlacesServiceStatus.OK && place) resolve(place);
                            else reject(new Error(`PlacesService: ${status}`));
                        }
                    );
                });

                logDebug('log', '✅ Place details (legacy):', placeDetails);

                latitude  = placeDetails.geometry?.location?.lat();
                longitude = placeDetails.geometry?.location?.lng();
                formattedAddress = placeDetails.formatted_address || suggestion.description;

                (placeDetails.address_components || []).forEach((component) => {
                    const types = component.types;
                    if (types.includes('locality'))                      city      = component.long_name;
                    else if (types.includes('administrative_area_level_1')) stateCode = component.short_name;
                    else if (types.includes('postal_code'))              zipCode   = component.long_name;
                });
            }

            const locationData = {
                latitude,
                longitude,
                city,
                stateCode,
                zipCode,
                formattedAddress,
                source: 'google-autocomplete'
            };

            logDebug('log', '✅ Parsed location data from Google:', locationData);

            // Validate the location is in the correct state
            if (!validateLocationState(locationData)) {
                setIsLoadingLocation(false);
                return;
            }

            // Pass the full location object with coordinates
            if (onLocationSelect) {
                onLocationSelect(locationData);
            }

            setIsLoadingLocation(false);
            onClose();
        } catch (error) {
            logDebug('error', '❌ Failed to get place details:', error?.message || error);
            // Fallback: try geocoding the address via MCS
            try {
                logDebug('log', '🔄 Falling back to MCS geocoding...');
                const coordsData = await locationService.geocodeMCS({ 
                    address1: suggestion.description 
                });
                
                // Validate the location is in the correct state
                if (!validateLocationState(coordsData)) {
                    setIsLoadingLocation(false);
                    return;
                }
                
                if (onLocationSelect) {
                    onLocationSelect(coordsData);
                }
                
                setIsLoadingLocation(false);
                onClose();
            } catch (mcsError) {
                logDebug('error', '❌ MCS geocoding also failed:', mcsError?.message || mcsError);
                setStateError('Unable to get coordinates for that location. Please try another address.');
                setIsLoadingLocation(false);
            }
        }
    };

    const handleUseCurrentLocation = async () => {
        setIsLoadingLocation(true);

        if (!('geolocation' in navigator)) {
            setStateError('Geolocation is not supported by your browser.');
            setIsLoadingLocation(false);
            return;
        }

        try {
            logDebug('log', '📍 Getting current location...');
            
            // Use locationService to get current location with MCS reverse geocoding
            const locationData = await locationService.getCurrentLocation();
            
            logDebug('log', '✅ Current location data:', locationData);

            // Validate the location is in the correct state
            if (!validateLocationState(locationData)) {
                setIsLoadingLocation(false);
                return;
            }

            if (onLocationSelect) {
                onLocationSelect(locationData);
            }

            setIsLoadingLocation(false);
            onClose();
        } catch (error) {
            logDebug('error', '❌ Geolocation error:', error?.message || error);
            // Previously this ALWAYS showed the "check your browser permissions" message for
            // any failure here, regardless of the actual cause — including
            // POSITION_UNAVAILABLE, TIMEOUT, and even plan-state mismatches that had been
            // (incorrectly) surfaced as a thrown error elsewhere in the chain. That produced
            // the confusing "permission" message even when the user's browser permission was
            // never actually denied (or wasn't even prompted), which testers correctly flagged
            // as "when should this come up? Seems not quite right." Branch on
            // locationService.getCurrentLocation()'s actual error.message (see its own
            // PERMISSION_DENIED/POSITION_UNAVAILABLE/TIMEOUT switch) so only a genuine
            // permission denial shows the permissions-specific copy; every other failure gets
            // wording that matches what actually happened.
            const message = error?.message || '';
            if (message.includes('denied')) {
                setStateError('Be sure to check your browser permissions for location. This allows the site to find providers nearby.');
            } else if (message.includes('unavailable')) {
                setStateError('Your current location could not be determined. Please try again, or enter your address manually below.');
            } else if (message.includes('timed out')) {
                setStateError('Finding your current location took too long. Please try again, or enter your address manually below.');
            } else if (message.includes('not supported')) {
                setStateError('Geolocation is not supported by your browser.');
            } else {
                setStateError('Unable to get your current location right now. Please try again, or enter your address manually below.');
            }
            setIsLoadingLocation(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') onClose();
    };

    const handleOverlayClick = (e) => {
        if (e.target.classList.contains('panel-overlay')) onClose();
    };

    if (!isOpen) return null;

    return (
        <div 
            className="panel-overlay" 
            onClick={handleOverlayClick}
            onKeyDown={handleKeyDown}
            role="dialog"
            aria-modal="true"
            aria-labelledby="location-panel-title"
        >
            <div className="side-panel location-panel" ref={panelRef}>
                {/* Panel Header */}
                <div className="panel-header">
                    <div className="panel-title-row">
                        <h2 id="location-panel-title" className="panel-title">Location</h2>
                        <button 
                            className="close-button"
                            onClick={onClose}
                            aria-label="Close location panel"
                        >
                            ×
                        </button>
                    </div>
                </div>

                {/* Panel Content */}
                <div className="panel-content">
                    <p className="location-info-text">
                        Fill in a city, county or ZIP code in {planFeatures?.planName || 'your state'}. Or search near your current location.
                    </p>

                    {/* Search Form */}
                    <form onSubmit={handleApplySearch} className="location-search-form">
                        <label htmlFor="location-search-input" className="location-search-label">
                            Search near
                        </label>

                        {/* Maps error warning - still allows manual input */}
                        {mapsError && (
                            <ApiErrorBanner type={ERROR_TYPES.LOCATION} variant="panel" />
                        )}

                        <div className="location-search-input-wrapper">
                            <input
                                id="location-search-input"
                                type="text"
                                className={`location-search-input ${stateError ? 'error' : ''}`}
                                value={searchValue}
                                onChange={handleSearchChange}
                                placeholder="Enter city, county or ZIP code"
                                aria-label="Search for location"
                                aria-autocomplete="list"
                                aria-controls="location-suggestions"
                                aria-describedby={stateError ? 'location-error-msg' : undefined}
                                autoFocus
                                autoComplete="off"
                            />
                            {searchValue && (
                                <button
                                    type="submit"
                                    className="location-search-submit"
                                    aria-label="Submit location search"
                                    disabled={isLoadingLocation}
                                >
                                    {isLoadingLocation ? 'Searching...' : 'Search'}
                                </button>
                            )}
                        </div>

                        {/* Error Message - suppressed while the maps/location-services
                            error banner above is showing, so the panel doesn't display
                            two overlapping error states at once. */}
                        {stateError && !mapsError && (
                            <div id="location-error-msg" className="location-error-message" role="alert">
                                <img src={hazardIcon} alt="" className="error-icon" />
                                <span>{stateError}</span>
                            </div>
                        )}

                        {/* Loading indicator */}
                        {isLoadingSuggestions && (
                            <div className="location-suggestions-loading">
                                Searching...
                            </div>
                        )}

                        {/* Suggestions List from Google Places */}
                        {suggestions.length > 0 && (
                            <ul
                                id="location-suggestions"
                                className="location-suggestions-list"
                                role="listbox"
                                aria-label="Address suggestions"
                            >
                                {suggestions.map((suggestion) => (
                                    <li key={suggestion.place_id} role="option">
                                        <button
                                            type="button"
                                            className="location-suggestion-item"
                                            onClick={() => handleSuggestionClick(suggestion)}
                                        >
                                            {/* Bold the matched part from user input */}
                                            <span className="suggestion-main">
                                                {suggestion.structured_formatting?.main_text || suggestion.description}
                                            </span>
                                            {suggestion.structured_formatting?.secondary_text && (
                                                <span className="suggestion-secondary">
                                                    {suggestion.structured_formatting.secondary_text}
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </form>

                    {/* Current Location Button */}
                    <button
                        type="button"
                        className="use-current-location-button"
                        onClick={handleUseCurrentLocation}
                        disabled={isLoadingLocation}
                    >
                        <img src={locationIcon} alt="" className="location-icon" aria-hidden="true" />
                        {isLoadingLocation ? 'Getting location...' : 'Use my current location'}
                    </button>

                    {/* State Selection Message */}
                    <p className="location-state-message">
                        Looking for providers in another state? Start on the{' '}
                        <a 
                            href="https://www.aetnabetterhealth.com/find-provider.html" 
                            target="_blank" 
                            rel="noopener noreferrer"
                        >
                            main site
                        </a>
                        . Then choose the state you want.
                    </p>
                </div>

                {/* Panel Footer */}
                <div className="panel-footer">
                    <button
                        type="button"
                        className="location-apply-button"
                        onClick={handleApplySearch}
                        disabled={isLoadingLocation || !searchValue.trim()}
                    >
                        Apply new location
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LocationPanel;
